import React, { useState } from 'react';
import { api } from '../../services/api';

export enum RefundType {
  PRODUCT_MISSING = 'product_missing',
  PRODUCT_DEFECTIVE = 'product_defective',
  WEIGHT_DIFFERENCE = 'weight_difference',
  ORDER_CANCELLED = 'order_cancelled',
  GOODWILL_GESTURE = 'goodwill_gesture',
}

export const REFUND_TYPE_LABELS = {
  [RefundType.PRODUCT_MISSING]: 'Produit manquant',
  [RefundType.PRODUCT_DEFECTIVE]: 'Produit défectueux',
  [RefundType.WEIGHT_DIFFERENCE]: 'Différence de poids',
  [RefundType.ORDER_CANCELLED]: 'Commande annulée',
  [RefundType.GOODWILL_GESTURE]: 'Geste commercial',
};

export const REFUND_REASONS = {
  [RefundType.PRODUCT_MISSING]: [
    'Produit non présent dans le colis',
    'Produit oublié lors de la préparation',
    'Produit en rupture de stock',
  ],
  [RefundType.PRODUCT_DEFECTIVE]: [
    'Produit périmé',
    'Produit endommagé',
    'Produit ne correspond pas à la description',
    'Problème de qualité',
  ],
  [RefundType.WEIGHT_DIFFERENCE]: [
    'Poids inférieur à la commande',
    'Différence de poids importante',
  ],
  [RefundType.ORDER_CANCELLED]: [
    'Annulation client',
    'Annulation magasin',
    'Erreur de commande',
  ],
  [RefundType.GOODWILL_GESTURE]: [
    'Geste commercial - retard',
    'Geste commercial - qualité',
    'Geste commercial - autre',
  ],
};

interface RefundModalProps {
  open: boolean;
  onClose: () => void;
  orderId: string;
  orderNumber: string;
  orderTotal: number; // en centimes
  onRefundCreated?: () => void;
}

export const RefundModal: React.FC<RefundModalProps> = ({
  open,
  onClose,
  orderId,
  orderNumber,
  orderTotal,
  onRefundCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [refundType, setRefundType] = useState<RefundType>(RefundType.PRODUCT_MISSING);
  const [reason, setReason] = useState('');
  const [amountEur, setAmountEur] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');

  const handleClose = () => {
    if (!loading) {
      setRefundType(RefundType.PRODUCT_MISSING);
      setReason('');
      setAmountEur('');
      setDescription('');
      setNotes('');
      setError(null);
      setSuccess(false);
      onClose();
    }
  };

  const handleSubmit = async () => {
    setError(null);
    setSuccess(false);

    // Validation
    if (!refundType) {
      setError('Veuillez sélectionner un type de remboursement');
      return;
    }

    if (!reason) {
      setError('Veuillez sélectionner un motif');
      return;
    }

    const amount = parseFloat(amountEur);
    if (isNaN(amount) || amount <= 0) {
      setError('Veuillez saisir un montant valide');
      return;
    }

    const amountCents = Math.round(amount * 100);
    const orderTotalEur = orderTotal / 100;

    if (amountCents > orderTotal) {
      setError(`Le montant ne peut pas dépasser le total de la commande (${orderTotalEur.toFixed(2)}€)`);
      return;
    }

    setLoading(true);

    try {
      // Créer le remboursement
      // `api.post` rend déjà le corps de la réponse : le remboursement créé.
      // L'ancien `createResponse.data.id` levait une erreur juste après la
      // création — rien n'était crédité, et chaque essai laissait un
      // remboursement « en attente » de plus (revue du 12/10/2026).
      const createResponse = await api.post<{ id?: string; data?: { id?: string } }>('/refunds', {
        order_id: orderId,
        amount_cents: amountCents,
        refund_type: refundType,
        reason,
        description: description || undefined,
        notes: notes || undefined,
      });

      const refundId = createResponse?.id ?? createResponse?.data?.id;
      if (!refundId) throw new Error('Remboursement créé mais identifiant absent de la réponse');

      // Approuver automatiquement le remboursement
      await api.post(`/refunds/${refundId}/approve`);

      setSuccess(true);

      // Notifier le parent
      if (onRefundCreated) {
        onRefundCreated();
      }

      // Fermer après 1.5 secondes
      setTimeout(() => {
        handleClose();
      }, 1500);
    } catch (err: any) {
      console.error('Erreur lors de la création du remboursement:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création du remboursement');
    } finally {
      setLoading(false);
    }
  };

  const availableReasons = REFUND_REASONS[refundType] || [];

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Créer un remboursement</h2>
          <p className="text-sm text-gray-600 mt-1">Commande: {orderNumber}</p>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {success && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800 font-semibold">
                ✓ Remboursement créé et approuvé avec succès !
              </p>
            </div>
          )}

          {/* Type de remboursement */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Type de remboursement *
            </label>
            <select
              value={refundType}
              onChange={(e) => {
                setRefundType(e.target.value as RefundType);
                setReason(''); // Réinitialiser le motif
              }}
              disabled={loading || success}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              {Object.entries(REFUND_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          {/* Motif */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motif *
            </label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading || success}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
            >
              <option value="">Sélectionnez un motif</option>
              {availableReasons.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          {/* Montant */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Montant du remboursement *
            </label>
            <div className="relative">
              <input
                type="number"
                value={amountEur}
                onChange={(e) => setAmountEur(e.target.value)}
                disabled={loading || success}
                step="0.01"
                min="0"
                max={(orderTotal / 100).toString()}
                className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                placeholder="0.00"
              />
              <span className="absolute right-3 top-2 text-gray-500">€</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Total commande: {(orderTotal / 100).toFixed(2)}€
            </p>
          </div>

          {/* Description (optionnelle) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description additionnelle (optionnelle)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={loading || success}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Ex: Le client a contacté le service client..."
            />
          </div>

          {/* Notes internes (optionnelles) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Notes internes (optionnelles)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={loading || success}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
              placeholder="Notes visibles uniquement par l'équipe..."
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || success}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {loading && (
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            )}
            {loading ? 'Création...' : 'Créer et approuver'}
          </button>
        </div>
      </div>
    </div>
  );
};
